import { ArrowRight } from "lucide-react";
import type { CtaButtonComponent } from "../../types";
import { RuntimeLink } from "../../runtime/RendererRuntime";

/**
 * Standalone call-to-action button. Three styles on the brand palette
 * (primary blue pill, secondary outlined, ghost underlined link) and a
 * simple horizontal alignment. Renders nothing without a label.
 */

const STYLE_CLASSES = {
  primary:
    "inline-flex items-center gap-2 rounded-full bg-[#005CA9] px-5 py-2.5 text-sm xl:text-base font-semibold text-white transition hover:bg-[#0F2849]",
  secondary:
    "inline-flex items-center gap-2 rounded-full border border-[#005CA9] px-5 py-2.5 text-sm xl:text-base font-semibold text-[#005CA9] transition hover:bg-[#005CA9]/5",
  ghost:
    "inline-flex items-center gap-2 text-sm xl:text-base font-semibold text-[#005CA9] underline underline-offset-4 transition hover:text-[#0F2849]",
} as const;

const ALIGN_CLASSES = {
  left: "justify-start",
  center: "justify-center",
  right: "justify-end",
} as const;

type CtaStyle = keyof typeof STYLE_CLASSES;
type CtaAlign = keyof typeof ALIGN_CLASSES;

function resolveStyle(style: unknown): CtaStyle {
  return typeof style === "string" && style in STYLE_CLASSES ? (style as CtaStyle) : "primary";
}

function resolveAlign(align: unknown): CtaAlign {
  return typeof align === "string" && align in ALIGN_CLASSES ? (align as CtaAlign) : "left";
}

/** Renderer for the standalone CTA button. */
export function CtaButtonRenderer({ component }: { component: CtaButtonComponent }) {
  const content = component.content ?? ({} as CtaButtonComponent["content"]);
  const label = content.label?.trim();
  if (!label) return null;

  return (
    <section data-cta-button>
      <div className={`flex ${ALIGN_CLASSES[resolveAlign(content.align)]}`}>
        <RuntimeLink href={content.href} className={STYLE_CLASSES[resolveStyle(content.style)]}>
          {label}
          <ArrowRight className="h-4 w-4 shrink-0" />
        </RuntimeLink>
      </div>
    </section>
  );
}
